"use client";

import Image from "next/image";
import {
    Bot,
    ChartNoAxesCombined,
    CircleDollarSign,
    Disc,
    FileCode2,
    FolderClosed,
    House,
    LucideIcon,
    MonitorPlay,
    MousePointerClick,
    ShieldCheck,
    Upload,
    Video,
} from "lucide-react";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
} from "@/components/ui/sidebar";
import { HeroIcon, HOMEPAGE } from "@/lib/utils";
import { useLocale } from "@/hooks/useLocale";
import { t } from "@/lib/localization";
import { PermissionKey } from "@/features/admin/types";
import SidebarNavSection from "./navSection";
import SidebarAlerts from "./sidebarAlerts";
import UserButton from "./userButton";

export type NavigationLeaf = {
    keyName: string;
    href: string;
    icon: HeroIcon | LucideIcon;
    requiredPermission?: PermissionKey;
    comingSoon?: boolean;
};

export type NavigationGroup = {
    keyName: string;
    icon: HeroIcon | LucideIcon;
    adminOnly?: boolean;
    children: {
        keyName: string;
        href: string;
        requiredPermission?: PermissionKey;
    }[];
};

export type NavigationItem = NavigationLeaf | NavigationGroup;

export type NavigationSection = {
    keyName: string;
    items: NavigationItem[];
};

const sections: NavigationSection[] = [
    {
        keyName: "platform.sidebar.section.platform",
        items: [
            {
                keyName: "platform.sidebar.home",
                href: HOMEPAGE,
                icon: House,
            },
            {
                keyName: "platform.sidebar.storage",
                href: "/storage",
                icon: FolderClosed,
            },
            {
                keyName: "platform.sidebar.chat-bots",
                href: "/assistants/v2",
                icon: Bot,
            },
        ],
    },
    {
        keyName: "platform.sidebar.section.tools",
        items: [
            {
                keyName: "platform.sidebar.tools.html-lomda",
                href: "/ai/create-lomda-v3",
                icon: MousePointerClick,
            },
            {
                keyName: "platform.sidebar.tools.video-lomda",
                href: "/ai/create-lomda-v2",
                icon: MonitorPlay,
            },
            {
                keyName: "platform.sidebar.tools.record-presentation",
                href: "/create-presentation",
                icon: Disc,
            },
            {
                keyName: "platform.sidebar.tools.upload-videos",
                href: "/ai/upload-videos",
                icon: Upload,
            },
        ],
    },
    {
        keyName: "platform.sidebar.section.account",
        items: [
            {
                keyName: "platform.sidebar.analytics",
                href: "/analytics",
                icon: ChartNoAxesCombined,
            },
            {
                keyName: "platform.sidebar.pricing",
                href: "/settings/pricing",
                icon: CircleDollarSign,
            },
            {
                keyName: "platform.sidebar.admin",
                icon: ShieldCheck,
                adminOnly: true,
                children: [
                    {
                        keyName: "platform.sidebar.admin.users",
                        href: "/admin/users",
                        requiredPermission: "Users",
                    },
                    {
                        keyName: "platform.sidebar.admin.content",
                        href: "/admin/content",
                        requiredPermission: "Content",
                    },
                    {
                        keyName: "platform.sidebar.admin.admins",
                        href: "/admin/manage",
                        requiredPermission: "ManageAdmins",
                    },
                    {
                        keyName: "platform.sidebar.admin.design",
                        href: "/admin/design",
                        requiredPermission: "Design",
                    },
                    {
                        keyName: "platform.sidebar.admin.dashboards",
                        href: "/admin/dashboards",
                        requiredPermission: "Billing",
                    },
                    {
                        keyName: "platform.sidebar.admin.billing-reports",
                        href: "/admin/billing-reports",
                        requiredPermission: "Billing",
                    },
                ],
            },
        ],
    },
];

export default function AppSidebar({ logo }: { logo: string }) {
    const { dir } = useLocale();

    return (
        <Sidebar collapsible="icon" side={dir === "rtl" ? "right" : "left"}>
            <SidebarHeader>
                <div className="flex items-center px-2 pb-1 pt-2">
                    <Image
                        alt={t("platform.sidebar.logo.alt")}
                        src={logo}
                        height={22}
                        width={107}
                        className="group-data-[collapsible=icon]:hidden"
                    />
                    <Image
                        alt={t("platform.sidebar.logo.alt")}
                        src="/images/branding/favicon/yeda.png"
                        height={24}
                        width={24}
                        className="mx-auto hidden size-6 group-data-[collapsible=icon]:block"
                    />
                </div>
            </SidebarHeader>
            <SidebarContent>
                {sections.map((section) => (
                    <SidebarNavSection
                        key={section.keyName}
                        section={section}
                    />
                ))}
            </SidebarContent>
            <SidebarFooter>
                <SidebarAlerts />
                <UserButton />
            </SidebarFooter>
        </Sidebar>
    );
}
